/**
 * Integritetspolicy för publika koder: the customer's own policy (link and/or
 * text), shown to people who scan a code placed in a public place.
 */
import React, { useEffect, useState } from 'react';
import { Card, Button, Alert, Form, Spinner } from 'react-bootstrap';
import ConfirmationModal from '../../Components/ConfirmationModal';
import { PrivacyPolicyInput } from '../../api/publicReportPrivacyApi';
import { PublicReportPrivacyPolicy, MAX_PRIVACY_POLICY_TEXT_LENGTH } from '../../types/publicReport';

interface PublicReportPrivacyCardProps {
    policy: PublicReportPrivacyPolicy | null;
    saving: boolean;
    /** Number of active codes placed in a public place, for the clear warning */
    publicCodeCount: number;
    onSave: (input: PrivacyPolicyInput) => Promise<void>;
    onClear: () => Promise<void>;
}

const PublicReportPrivacyCard: React.FC<PublicReportPrivacyCardProps> = ({
    policy,
    saving,
    publicCodeCount,
    onSave,
    onClear,
}) => {
    const [url, setUrl] = useState(policy?.url || '');
    const [text, setText] = useState(policy?.text || '');
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);
    const [showClearModal, setShowClearModal] = useState(false);

    useEffect(() => {
        setUrl(policy?.url || '');
        setText(policy?.text || '');
    }, [policy]);

    const dirty = url !== (policy?.url || '') || text !== (policy?.text || '');

    const handleSave = async () => {
        setError(null);
        setSaved(false);
        try {
            await onSave({ url, text });
            setSaved(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte spara integritetspolicyn');
        }
    };

    const handleClear = async () => {
        setShowClearModal(false);
        setError(null);
        setSaved(false);
        try {
            await onClear();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte ta bort integritetspolicyn');
        }
    };

    const clearMessage = publicCodeCount > 0
        ? `${publicCodeCount} ${publicCodeCount === 1 ? 'kod' : 'koder'} på allmän plats visar då ingen integritetspolicy. Vill du ta bort den?`
        : 'Vill du ta bort integritetspolicyn?';

    return (
        <Card className="std-box dropshadow mb-4" style={{ border: 'none' }}>
            <Card.Body style={{ padding: '20px' }}>
                <h5 style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>Integritetspolicy för publika koder</h5>
                <p className="text-muted" style={{ fontSize: '14px' }}>
                    Visas för den som skannar en QR-kod som placerats på allmän plats. Ni som sätter upp koden
                    är personuppgiftsansvariga och ansvarar för policyns innehåll. Ange en länk till er
                    befintliga policy, en text, eller båda.
                </p>

                <div className="public-report-privacy-facts mb-3">
                    <strong>Det här behöver ni känna till när ni skriver policyn</strong>
                    <ul className="mb-1">
                        <li>Sidan samlar in en beskrivning av felet och upp till 5 bilder.</li>
                        <li>Namn, telefonnummer och e-post samlas inte in, men den som skannar kan av misstag skriva eller fotografera personuppgifter, till exempel ett namn i beskrivningen eller en person på en bild.</li>
                        <li>Uppgifterna lagras i Google Firebase.</li>
                    </ul>
                    <span>
                        Policyn bör ange vem som är personuppgiftsansvarig, ändamål och rättslig grund, mottagare,
                        lagringstid, de registrerades rättigheter och en kontaktväg. Det här är fakta om systemet,
                        inte juridisk rådgivning.
                    </span>
                </div>

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                {saved && !dirty && (
                    <Alert variant="success" dismissible onClose={() => setSaved(false)}>
                        Integritetspolicyn sparad
                    </Alert>
                )}

                <Form.Group className="mb-3" controlId="public-report-privacy-url">
                    <Form.Label style={{ fontSize: '14px' }}>Länk till integritetspolicy</Form.Label>
                    <Form.Control
                        type="url"
                        inputMode="url"
                        placeholder="https://"
                        value={url}
                        onChange={e => setUrl(e.target.value)}
                        disabled={saving}
                    />
                </Form.Group>

                <Form.Group className="mb-3" controlId="public-report-privacy-text">
                    <Form.Label style={{ fontSize: '14px' }}>Text</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={6}
                        value={text}
                        onChange={e => setText(e.target.value)}
                        maxLength={MAX_PRIVACY_POLICY_TEXT_LENGTH}
                        disabled={saving}
                    />
                    <Form.Text className="text-muted">
                        Visas som vanlig text med radbrytningar.
                    </Form.Text>
                </Form.Group>

                <div className="d-flex gap-2">
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSave}
                        disabled={saving || !dirty || (!url.trim() && !text.trim())}
                    >
                        {saving ? (
                            <><Spinner animation="border" size="sm" className="me-2" />Sparar...</>
                        ) : (
                            'Spara integritetspolicy'
                        )}
                    </Button>
                    {policy && (
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => setShowClearModal(true)}
                            disabled={saving}
                        >
                            Ta bort
                        </Button>
                    )}
                </div>
            </Card.Body>

            <ConfirmationModal
                show={showClearModal}
                title="Ta bort integritetspolicy"
                message={clearMessage}
                onConfirm={handleClear}
                onCancel={() => setShowClearModal(false)}
                loading={saving}
            />
        </Card>
    );
};

export default PublicReportPrivacyCard;
